import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatorMode } from '../hooks/useTranslatorMode';
import { i18nAPI } from '../services/api';

/**
 * <T> — Translatable text component
 *
 * Drop-in replacement for {t('key')} that adds in-context translation
 * editing when the user has the TRANSLATOR role and is viewing a non-English locale.
 *
 * Usage:
 *   Before: <span>{t('venue.photos')}</span>
 *   After:  <span><T k="venue.photos" /></span>
 *
 * Props:
 *   k         {string}   i18n key (e.g. 'venue.photos')
 *   values    {object}   interpolation values (e.g. { count: 5 })
 *   tag       {string}   wrapper element (default: 'span'). Use 'div', 'p', 'h2', etc.
 *   className {string}   CSS class to apply to the wrapper
 *   style     {object}   inline styles for the wrapper
 *
 * In normal mode: renders t(k, values) as plain text with zero visual overhead.
 * In translator mode: adds a subtle pencil icon. Click opens inline editor.
 */
export function T({ k, values, tag = 'span', className, style }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const { isTranslator } = useTranslatorMode();
  const [editing, setEditing] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const popoverRef = useRef(null);

  const currentValue = t(k, values);
  const isEnglish = locale === 'en';
  const showPencil = isTranslator && !isEnglish;

  // Close popover on outside click
  useEffect(() => {
    if (!editing) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing]);

  const handleSubmit = async () => {
    if (!suggestion.trim()) return;
    setSubmitting(true);
    try {
      await i18nAPI.submitUICorrection({
        key: k,
        locale,
        currentValue,
        suggestedValue: suggestion.trim(),
      });
      // Immediately update the local i18n resource so the change is visible
      const parts = k.split('.');
      const patch = {};
      let obj = patch;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = suggestion.trim();
      i18n.addResourceBundle(locale, 'translation', patch, true, true);

      setSubmitted(true);
      setTimeout(() => {
        setEditing(false);
        setSubmitted(false);
        setSuggestion('');
      }, 1500);
    } catch (_e) {
      // Silent
    } finally {
      setSubmitting(false);
    }
  };

  const Tag = tag;

  // Normal mode: just render the text
  if (!showPencil) {
    return <Tag className={className} style={style}>{currentValue}</Tag>;
  }

  // Translator mode: text + pencil icon
  return (
    <Tag
      className={className}
      style={{ ...style, position: 'relative', display: 'inline' }}
    >
      {currentValue}
      <span
        ref={popoverRef}
        style={{ position: 'relative', display: 'inline-block', marginLeft: 3 }}
      >
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSuggestion(currentValue);
            setEditing(!editing);
          }}
          title={t('common.editTranslationTitle', { key: k })}
          aria-label={t('common.editTranslationAria', { key: k })}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            opacity: editing ? 1 : 0.4,
            padding: '0 1px',
            lineHeight: 1,
            verticalAlign: 'super',
            transition: 'opacity 0.15s',
            color: '#818cf8',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) =>
            (e.currentTarget.style.opacity = editing ? '1' : '0.4')
          }
        >
          ✎
        </button>

        {editing && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 9999,
              background: '#1a1a2e',
              border: '1px solid #6366f1',
              borderRadius: 8,
              padding: 12,
              width: 260,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              marginTop: 4,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {submitted ? (
              <div style={{ color: '#22c55e', textAlign: 'center', padding: '6px 0', fontSize: 13, fontWeight: 600 }}>
                {t('common.updatedReloading')}
              </div>
            ) : (
              <>
                <div style={{ color: '#6366f1', fontSize: 10, marginBottom: 4, fontFamily: 'monospace', opacity: 0.7 }}>
                  {k}
                </div>
                <textarea
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    background: '#0f0f0f',
                    border: '1px solid #333',
                    borderRadius: 5,
                    color: '#e0e0e0',
                    padding: 6,
                    fontSize: 13,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !suggestion.trim() || suggestion.trim() === currentValue}
                    style={{
                      flex: 1,
                      background: '#6366f1',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      padding: '5px 0',
                      cursor: submitting || !suggestion.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: 12,
                      opacity: submitting || !suggestion.trim() || suggestion.trim() === currentValue ? 0.5 : 1,
                    }}
                  >
                    {submitting ? t('common.saving') : t('common.save')}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setSuggestion(''); }}
                    style={{
                      background: 'transparent',
                      color: '#888',
                      border: '1px solid #333',
                      borderRadius: 5,
                      padding: '5px 10px',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </span>
    </Tag>
  );
}

export default T;
